import config from './config'
import { Dispatch, SetStateAction } from 'react'
import { toastType, ToastTypes } from '@yaak/components/src/Toast/Toast'
import { IntlShape } from "react-intl/src/types";
import { FeatureFlags } from "../../src/FeatureFlagProvider/FeatureFlagProvider";
import { apiFetch, defaultHeaders } from './headers'

export type ExamResult = {
  passed: boolean
  timestamp: string
  userLicenseId: string | null
}

export type MainContact = {
  id: string
  firstName: string
  lastName: string
  profileImageURL: string
  mainContactId: string
}

export type Partner = {
  id: string
  name: string
  language: string | null
  mainContact: MainContact | null
  mainContactId: string | null
  simEnabled: boolean
  plans: string[]
}

export type UpdatePartner = {
  id: string
  name: string
  language: string | null
  mainContact: MainContact | null
  mainContactId: string | null
  simEnabled: boolean
  plans: string
}

export type CreatePartner = {
  name: string
  language: string | null
  simEnabled: boolean
  plans: string
}

export type UserLicense = {
  finishedAt: string | null
  id: string
  licenseCode: string
  licenseId: string
  userId: string
  vehicleTypeId: string
  vehicleTypeName: string
  disabledAt: string | null
  category?: string | null
  trainingPathId?: string | null
}

export type LicenseType = {
  id: string
  code: string
  name: string
  description?: string
}

export type UserInstructor = {
  id: string
  firstName: string
  lastName: string
  isDefault: boolean
}

export type User = {
  id: string
  firstName: string
  lastName: string
  email: string | null
  phoneNumber?: string
  roles?: string[]
  partnerId: string
  partner?: Partner
  active: boolean
  preferredLanguage?: string
  profileImageURL: string
  tosAccepted?: boolean
  rating: number | null
  durationMinutes?: number
  examResults: Map<string, ExamResult[]> | { [licenseCode: string]: ExamResult[] } | null
  examResultsRaw?: any
  simEnabled?: boolean
  driveCount?: number
  currentMonthExpertMinutes?: number
  drivingLicenses?: string[]
  licenses: UserLicense[]
  licensesType?: string[]
  passedExamPerLicense?: Map<string, boolean>
  instructors?: UserInstructor[]
}

export type InstructorStats = {
  currentMonthExpertMins: number
  id: string
  students: User[]
  totalExpertMins: number
}

export type CreateUser = {
  firstName: string
  lastName: string
  email: string
  roles: string[]
  partnerId: string | null
  phoneNumber?: string | null
  preferredLanguage: string | null
  sendEmail: boolean
  licenses?: string[]
  LicensesType?: string[]
  simEnabled?: boolean
  defaultInstructor?: string | null
}

export type InviteStudentLicense = {
  learningPathId: string
  licenseTypeId: string
}

export type InviteStudent = {
  defaultInstructor?: string | null
  email: string
  firstName: string
  lastName: string
  licenses: InviteStudentLicense[]
  partnerId: string
  phoneNumber?: string | null
  preferredLanguage?: string | null
  simEnabled?: boolean
}

export enum KitPowerState {
  TurnedOn = 'turned_on',
  TurningOn = 'turning_on',
  TurnedOff = 'turned_off',
  TurningOff = 'turning_off',
  Error = 'error',
}

export type Device = {
  dongleId: string
  status: string
  partnerId: string | null
  kitType: string | null
  licensePlate: string | null
  ipAddress: string
  active: boolean
  lastStatusUpdate: string
  mqttEnabled?: boolean
  modemStatus?: {
    uptime_ms: number
    state: KitPowerState
    error?: string
    voltages: {
      battery: number
      v12: number
    }
    ublox: {
      rsrq: number
      rsrp: number
    }
  }
  pairedVehicleVIN: string
}

export type Driver = {
  firstname: string
  id: string
  lastname: string
  profileImageURL: string
}

export type Incident = {
  id: string
  type: string
  timestamp: string
  thumbnailUrl?: string
  videoUrl?: string
}

export type Annotation = {
  id: string
  sessionId: string
  startTimestamp: string
  endTimestamp: string
  startOffset: number
  endOffset: number
  tag: string | null
  videoThumbnailUrl: string
  videoUrl: string
  shared: boolean
  shareWatched: boolean
  curriculumItems: string[]
}

export type AnnotationsResponse = {
  data: Annotation[]
  metaData: {
    count: number
    totalCount: number
  }
}

export type AdkTopicRating = {
  topic: string
  rating: number | null
}

export type SessionData = {
  active: boolean
  bitRateKbps?: number
  bookmarkCount: number
  canonicalName: string
  curriculumCount: number
  dongleId: string
  driveType: number
  driver: Driver | null
  driverDriveCount: number
  endTimestamp: string | null
  id: string
  incidentCount: number
  instructor: Driver | null
  licensePlate?: string
  licenseId?: string
  vin?: string
  location: {
    street: string | null
    area: string | null
    address: string | null
  }
  metadataStatus: number
  partner: Partner | null
  startTimestamp: string
  status: number
  videoStatus: number
  fullVideoStatus: number
  lessonTopics: Array<string>
  rating: number | null
  weatherConditions: string[]
  lightningConditions: string[]
  vehicleTypeName: string
  adkTopics?: string[] | null
  adkTopicsRating?: AdkTopicRating[] | null
  adkFlowKeys?: string[] | null
  incidents?: Incident[]
}

export type Session = {
  data: SessionData[]
  metaData: {
    count: number
    totalCount: number
  }
}

export type PairingRequest = {
  id: string
  vin: string
  kitId: string
  grantedBy?: User
  pairedAt?: string
  status: string
  calibrationAvailable: boolean
}

export type CreatePairingRequest = {
  vin: string
  kitId: string
}

export type CreateVehicle = {
  licensePlate: string | null
  partnerID: string | null
  vin: string | null
  friendlyName?: string
}

export type Vehicle = {
  calibrationAvailable?: boolean
  kit?: {
    id: string
    status: string
  }
  licensePlate: string
  partner: Partner | null
  vin: string
  friendlyName: string
}

export type VehicleResponse = {
  data: Vehicle[]
  metaData: {
    count: number
    totalCount: number
  }
}

type alertMessagesType = {
  success?: {
    text?: string
    textId?: string
  }
  error?: { text: string }
}

export type UsersResponse = {
  data: User[]
  metaData: {
    count: number
    totalCount: number
  }
}

const normalizeStudentsResponse = (parsed: unknown): UsersResponse | undefined => {
  if (!parsed) {
    return undefined
  }

  if (Array.isArray(parsed)) {
    return {
      data: parsed,
      metaData: { count: parsed.length, totalCount: parsed.length },
    }
  }

  if (typeof parsed === 'object' && parsed !== null && 'data' in parsed) {
    const response = parsed as UsersResponse
    return {
      data: Array.isArray(response.data) ? response.data : [],
      metaData: response.metaData ?? { count: 0, totalCount: 0 },
    }
  }

  return undefined
}

export type PairingResponse = {
  data: PairingRequest[]
  metaData: {
    count: number
    totalCount: number
  }
}

type RatingRecord = {
  timestamp: string
  durationMinutes: number
  rating: number | null
}

type RatingSummary = {
  rating: number
  durationMinutes: number
  ratingRecords: RatingRecord[]
}

export type LessonTopic = {
  name: string
  category: string
  mandatoryDrivingMinutes: number
  restrictedCurriculumItemSubset: string[] | null
  restrictedEventTagIdSubset: string[] | null
}

export type LessonTopics = {
  data: { [key: string]: LessonTopic[] }
  metaData: {
    count: number
    totalCount: number
  }
}

export type UserStats = {
  userId: string
  rating: number
  durationMinutes: number
  ratingRecords: RatingRecord[]
  lessonTopicRatings: { [id: string]: RatingSummary }
  lessonTopicCategoryRatings: { [id: string]: RatingSummary }
  licenseRating: LicenseRatingMap
}

interface CategoryRating {
  rating: number
  durationMinutes: number
}

export type LicenseRating = {
  rating: {
    rating: number
    duration: number
  }
  lessonTopicCategoryRatings: {
    [category: string]: CategoryRating
  }
}

export type LicenseRatingMap = {
  [licenseCode: string]: LicenseRating
}

export type LessonTopicCategoryRating = {
  category: string
  rating: number
}

export type PartnerStats = {
  activeInstructors: number
  activeStudents: number
  expertDriveMinutes: number
  studentDriveMinutes: number
}

export type SimDeviceInfo = {
  deviceId: string
  payload: string | Record<string, unknown>
  createdAt: string
}

export type SimRigsData = {
  id: string
  name: string
  version: string
  partner: Partner | null
  status: string
  info?: SimDeviceInfo | null
}

export type SimRigs = {
  data: SimRigsData[]
  metaData: {
    count: number
    totalCount: number
  }
}

export type SimMapConfigModule = {
  moduleId: string
  title: string
  type: string
  groupName: string
  displayIndex: number
  gearModes: string[]
  supportedVersions: string[]
  partnerDeviceCount: number
  enabled: boolean
}

export type SimMapConfigResponse = {
  partnerId: string
  modules: SimMapConfigModule[]
}

export type SimModuleCatalogEntry = {
  moduleId: string
  title: string
  type: string
  groupName: string
  displayIndex: number
  gearModes: string[]
  supportedVersions: string[]
  active: boolean
  isCustom: boolean
}

export type SimModuleAllowlistResponse = {
  moduleId: string
  partnerIds: string[]
}

export type SimModuleCatalogResponse = {
  modules: SimModuleCatalogEntry[]
}

export type SimulationDrive = {
  endTimestamp: string
  id: string
  module: {
    id: string
    header: string
    title: string
    description: string
    type: string
  }
  partner: {
    id: string
    name: string
  } | null
  rating: number
  result: string
  simDeviceId: string
  simDeviceName: string
  startTimestamp: string
  status?: string
  user: Driver
}

export type SimulationDuration = {
  totalDurationSeconds: number
}

export type SimulationDrives = {
  data: SimulationDrive[]
  duration?: SimulationDuration
  metaData: {
    count: number
    totalCount: number
  }
}

export type SimProgressLessonGroup = {
  id: string
  imageSource: string
  title: string
  header: string
  description: string
}

export type SimProgressCompletedLesson = {
  id: string
  title: string
  header: string
  description: string
}

export type SimProgressLesson = {
  id: string
  type: string
  unlocked: boolean
  result: string | null
  inProgress: boolean
  displayIndex: number
  title: string
  header: string
  description: string
  gearModes: string[]
  group: SimProgressLessonGroup | null
  rating: SimProgressRating | null
}

// Error category -> number of times the error occurred.
export type SimProgressRating = Record<string, number>

export type SimProgress = {
  completedCount: number
  totalCount: number
  percent: number
  lastCompletedLesson: SimProgressCompletedLesson | null
  lessons: SimProgressLesson[]
  rating: SimProgressRating
}

export type VideoStream = {
  cameraPosition: string
  id: string
  sessionId: string
  url: string
}

export type VideoStreams = {
  data: VideoStream[]
  metaData: {
    count: number
    totalCount: number
  }
}

export type Camera = {
  name: string
  url: string
}

export type SessionInfo = {
  cameras: Camera[]
  canonicalName: string
  dongleId: string
  endTimestamp: string
  geoCollection: { type: string, features: any }
  id: string
  metadata: string
  metadataMCAP: string
  startTimestamp: string
}

export type SessionTimestamp = {
  name: string
  startTimestamp: string
  endTimestamp: string
}

export type IncidentOutcome = {
  id: string
  label: string
  slug: string
}

export type IncidentOutcomes = {
  data: IncidentOutcome[]
  metaData: {
    count: number
    totalCount: number
  }
}

export type DriveMetric = {
  driveType: string
  duration: number
}

export type IncidentMetric = {
  tag: string
  count: number
}

export type LanesMetric = {
  lanes: string
  duration: number
}

export type LightningMetric = {
  condition: string
  duration: number
}

export type MapFeaturesMetric = {
  feature: string
  count: number
}

export type RoadTypeMetric = {
  type: string
  distance: number
}

export type SpeedHistogram = {
  bucket: number
  duration: number
  maxSpeed: number
  minSpeed: number
}

export type WeatherMetric = {
  condition: string
  duration: number
}

export type MetricsData = {
  avgDriveDurationMetric: DriveMetric[]
  drivingMetric: DriveMetric[]
  incidentMetric: IncidentMetric[]
  lanesMetric: LanesMetric[]
  lightningMetric: LightningMetric[]
  mapFeaturesMetric: MapFeaturesMetric[]
  roadTypeMetric: RoadTypeMetric[]
  speedHistogram: SpeedHistogram[]
  weatherMetric: WeatherMetric[]
}

export type PartnerSettings = {
  selfRegistration: boolean
  selfCancellation: boolean
  cancellationDeadline: number
  limitAppointmentCapacity: boolean
  appointmentRequiresModuleCompletion: boolean
}

export type TheoryClassesResponse = {
  classes: TheoryClass[]
}
export type Appointment = {
  start_date: string
  end_date: string
  theme: string
}

export type TheoryClass = {
  appointments: Appointment[]
  name: string
  capacity: number
  frequency: string
  themes: string[]
}

export type FeatureFlagAdmin = {
  id: string
  name: string
  description: string | null
  enabled: boolean
}

export type FeatureFlagEditableAdmin = {
  id: string
  description: string | null
  enabled: boolean
}

export type CreateFeatureFlag = {
  name: string
  description: string
  enabled: boolean
}

export type PruefungQuestionOption = {
  id: string
  description: string
  correct: boolean
  image: string
}

export type PruefungQuestionField = {
  id: string
  predefined: string
  answer: string
  order: number
}

export type PruefungStatistics = {
  total_passed: number
  total_failed: number
  average_fault_points: number
  pass_rate: number
}

export type PruefungTypeBreakdown = {
  type: string
  fault_points: number
}

export type PruefungCategoryBreakdown = {
  category: string
  type: string
  fault_points: number
}

export type PruefungBreakdown = {
  type_breakdown: PruefungTypeBreakdown[] | null
  category_breakdown: PruefungCategoryBreakdown[] | null
}

export type PruefungQuestion = {
  id: string
  license: string
  official_id: string
  score: number
  title: string
  subtitle: string
  image: string
  image_subtitle: string
  type: string
  video: string
  created_at: string
  options: PruefungQuestionOption[]
  user_correct: boolean
  order: number
  user_mcq_answers: string[]
  fields: PruefungQuestionField[]
  user_text_answer: string
}

export type PruefungAttempt = {
  id: string
  test_date: string
  finished_at: string
  time_taken_seconds: number
  passed: boolean
  fault_points: number
  fail_mark: number
  question_count: number
  correct_answers: number
  license_code: string
  language: string
  questions: PruefungQuestion[]
  breakdown: PruefungBreakdown
}

export type PruefungAttemptResponse = {
  data: PruefungAttempt[]
  statistics: PruefungStatistics
}

const processResponse = async ({
  response,
  onAlert,
  messages,
  auth0,
}: {
  response: Response
  onAlert?: Dispatch<SetStateAction<toastType | null>>
  messages?: alertMessagesType
  auth0?: boolean
}) => {
  if (
    response.status === 200 ||
    response.status === 201 ||
    response.status === 202 ||
    response.status === 204
  ) {
    messages?.success &&
      onAlert && onAlert({
        text: messages.success.text,
        type: messages.success ? ToastTypes.success : ToastTypes.error,
      })

    // In some cases, 200 may not have a body, but it is still valid.
    const responseBody = await response.text()
    if (responseBody.length < 1) {
      return
    }

    return auth0 ? responseBody : JSON.parse(responseBody)
  }

  // For non 200 cases, then we parse the output
  try {
    const text = await response.text()
    const parsed = JSON.parse(text)

    // Force logout if mentioned as such
    if (response.status === 403) {
      if (parsed.logout === true) {
        localStorage.clear()
      }
    }
    if (onAlert) {
      onAlert({
        text: `${parsed.code}: ${parsed.message}`,
        type: ToastTypes.error,
      })
    }
  } catch (e) {
    if (onAlert) {
      onAlert({
        text: `${response.status}: ${response.statusText}`,
        type: ToastTypes.error,
      })
    }
    console.error(response.status, response.statusText)
  }
}

type QueriesParam = {
  [key: string]: boolean | number | string | string[] | null | undefined
}

const parseQueryStrings = (queries: QueriesParam) => {
  const result = Object.keys(queries)
    .reduce(
      (pre: string[], curr: string) => {
        const value = queries[curr]
        const shouldInclude = value !== undefined
        if (!shouldInclude) return pre;

        const valueStr = value === null ? '' : String(value);
        return pre.concat(`${curr}=${encodeURIComponent(valueStr)}`);
      },
      []
    )
    .join('&')
  return result
}

const getSessionById = ({
  token,
  id,
  onAlert,
  includeSharedEvents = true,
}: {
  token: string
  id: string
  onAlert: Dispatch<SetStateAction<toastType | null>>
  includeSharedEvents?: boolean
}): Promise<SessionData> => {
  const queryStrings = parseQueryStrings({
    includeSharedEvents,
  })
  return apiFetch(`${config.apiUrl}/v2/sessions/${id}?${queryStrings}`, {
    credentials: 'same-origin',
    headers: defaultHeaders(token),
    method: 'GET',
  })
    .then((response) => processResponse({ response, onAlert }))
    .catch((error) => {
      onAlert({
        text: `${error.name}: ${error.message}`,
        type: ToastTypes.error,
      })
    })
}

const getLoggedInUser = async ({
  token,
  onAlert,
}: {
  token: string
  onAlert?: Dispatch<SetStateAction<toastType | null>>
}): Promise<User> => {
  return await apiFetch(`${config.apiUrl}/v2/users/me`, {
    credentials: 'same-origin',
    headers: defaultHeaders(token),
    method: 'GET',
  })
    .then((response) => processResponse({ response, onAlert }))
    .catch((error) => {
      if (onAlert) {
        onAlert({
          text: `${error.name}: ${error.message}`,
          type: ToastTypes.error,
        })
      }
      throw error
    })
}

const getUsers = async ({
  token,
  onAlert,
  roles,
  limit,
  offset,
  partnerId,
  withExpertStats = false,
  sortBy = 'firstName',
  sortOrder = 'DESC',
  searchQuery,
}: {
  token: string
  onAlert: Dispatch<SetStateAction<toastType | null>>
  roles?: string[]
  partnerId?: string
  withExpertStats?: boolean
  offset?: number
  sortBy?: string
  sortOrder?: string
  searchQuery?: string
  limit?: number
}): Promise<UsersResponse> => {


  const queryStrings = parseQueryStrings({
    limit,
    role: roles,
    offset,
    sort_by: sortBy,
    sort_order: sortOrder,
    partnerId,
    expertStats: withExpertStats,
    q: searchQuery,
  })

  return await apiFetch(`${config.apiUrl}/v2/users?${queryStrings}`, {
    credentials: 'same-origin',
    headers: defaultHeaders(token),
    method: 'GET',
  })
    .then((response) => processResponse({ response, onAlert }))
    .catch((error) => {
      onAlert({
        text: `${error.name}: ${error.message}`,
        type: ToastTypes.error,
      })
    })
}

const getStudents = async ({
  token,
  onAlert,
  limit,
  offset,
  q,
}: {
  token: string
  onAlert: Dispatch<SetStateAction<toastType | null>>
  offset?: number
  limit?: number
  q?: string
}): Promise<UsersResponse> => {
  const queryStrings = parseQueryStrings({
    limit,
    offset,
    q,
  })

  return await apiFetch(`${config.apiUrl}/v1/students?${queryStrings}`, {
    credentials: 'same-origin',
    headers: defaultHeaders(token),
    method: 'GET',
  })
    .then(async (response) =>
      normalizeStudentsResponse(await processResponse({ response, onAlert }))
    )
    .catch((error) => {
      onAlert({
        text: `${error.name}: ${error.message}`,
        type: ToastTypes.error,
      })
    })
}

const getPartnerStats = ({
  token,
  id,
  onAlert,
}: {
  token: string
  id: string
  onAlert: Dispatch<SetStateAction<toastType | null>>
}): Promise<{ [key: string]: PartnerStats }> => {
  return apiFetch(`${config.apiUrl}/v1/partners/${id}/stats`, {
    credentials: 'same-origin',
    headers: defaultHeaders(token),
    method: 'GET',
  })
    .then((response) => processResponse({ response, onAlert }))
    .catch((error) => {
      onAlert({
        text: `${error.name}: ${error.message}`,
        type: ToastTypes.error,
      })
    })
}

const getInstructorData = ({
  token,
  instructorID,
  partnerID,
  onAlert,
}: {
  token: string
  instructorID?: string
  partnerID?: string
  onAlert: Dispatch<SetStateAction<toastType | null>>
}): Promise<InstructorStats> => {
  return apiFetch(
    `${config.apiUrl}/v1/partners/${partnerID}/instructors/${instructorID}`,
    {
      credentials: 'same-origin',
      headers: defaultHeaders(token),
      method: 'GET',
    }
  )
    .then((response) => processResponse({ response, onAlert }))
    .catch((error) => {
      onAlert({
        text: `${error.name}: ${error.message}`,
        type: ToastTypes.error,
      })
    })
}

const getSuccessMessage = (user: CreateUser, intl: any) =>
  user.roles.indexOf('ds-admin') !== -1
    ? intl.formatMessage(
      {
        id: 'add_admin_success',
      },
      {
        name: `${user.firstName} ${user.lastName}`,
      }
    )
    : user.roles.indexOf('ds-instructor') !== -1
      ? intl.formatMessage(
        {
          id: 'add_instructor_success',
        },
        {
          name: `${user.firstName} ${user.lastName}`,
        }
      )
      : intl.formatMessage(
        {
          id: 'add_student_success',
        },
        {
          name: `${user.firstName} ${user.lastName}`,
        }
      )

const getDeleteUserMessage = (user: User, intl: any) =>
  user.roles?.indexOf('ds-admin') !== -1
    ? intl.formatMessage(
      {
        id: 'delete_admin_success',
      },
      {
        name: `${user.firstName} ${user.lastName}`,
      }
    )
    : user.roles.indexOf('ds-instructor') !== -1
      ? intl.formatMessage(
        {
          id: 'delete_instructor_success',
        },
        {
          name: `${user.firstName} ${user.lastName}`,
        }
      )
      : intl.formatMessage(
        {
          id: 'delete_student_success',
        },
        {
          name: `${user.firstName} ${user.lastName}`,
        }
      )

const getUpdateUserMessage = (user: User, intl: any) =>
  user.roles?.indexOf('ds-admin') !== -1
    ? intl.formatMessage(
      {
        id: 'update_admin_success',
      },
      {
        name: `${user.firstName} ${user.lastName}`,
      }
    )
    : user.roles.indexOf('ds-instructor') !== -1
      ? intl.formatMessage(
        {
          id: 'update_instructor_success',
        },
        {
          name: `${user.firstName} ${user.lastName}`,
        }
      )
      : intl.formatMessage(
        {
          id: 'update_student_success',
        },
        {
          name: `${user.firstName} ${user.lastName}`,
        }
      )

const getCreateUserNotification = (user: CreateUser) =>
  user.roles.indexOf('ds-admin') !== -1
    ? `DS Admin ${user.firstName} ${user.lastName} added successfully`
    : user.roles.indexOf('ds-student') !== -1
      ? `Student ${user.firstName} ${user.lastName} added successfully`
      : user.roles.indexOf('ds-instructor') !== -1
        ? `Instructor ${user.firstName} ${user.lastName} added successfully`
        : 'User added successfully'

const createUser = ({
  token,
  user,
  onAlert,
  intl,
}: {
  token: string
  user: CreateUser
  onAlert: Dispatch<SetStateAction<toastType | null>>
  intl?: any
}): Promise<User[]> => {
  if (user.licenses && user.licenses.length > 0) {
    user.LicensesType = user.licenses;
  }

  return apiFetch(
    `${config.apiUrl}/v2/users/invite?email=${user.sendEmail || false}`,
    {
      credentials: 'same-origin',
      headers: defaultHeaders(token),
      body: JSON.stringify(user),
      method: 'POST',
    }
  )
    .then(async (response) =>
      processResponse({
        response,
        onAlert,
        messages: {
          success: {
            text: intl
              ? getSuccessMessage(user, intl)
              : getCreateUserNotification(user),
          },
        },
      })
    )
    .catch((error) => {
      onAlert({
        text: `${error.name}: ${error.message}`,
        type: ToastTypes.error,
      })
    })
}

const createExamResult = ({
  token,
  user,
  examResult,
  onAlert,
  intl,
}: {
  token: string
  user: User
  examResult: ExamResult
  onAlert: Dispatch<SetStateAction<toastType | null>>
  intl: any
}): Promise<User[]> => {
  return apiFetch(`${config.apiUrl}/v1/users/${user.id}/exams`, {
    credentials: 'same-origin',
    headers: defaultHeaders(token),
    body: JSON.stringify(examResult),
    method: 'POST',
  })
    .then(async (response) => {
      return processResponse({
        response,
        onAlert,
        messages: {
          success: {
            text: intl.formatMessage(
              {
                id: 'add_exam_dialog.success',
              },
              {
                name: `${user.firstName} ${user.lastName}`,
              }
            ),
          },
        },
      })
    })
    .catch((error) => {
      onAlert({
        text: `${error.name}: ${error.message}`,
        type: ToastTypes.error,
      })
    })
}

const getUserStats = async ({
  userId,
  token,
  onAlert,
}: {
  userId: string
  token: string
  onAlert: Dispatch<SetStateAction<toastType | null>>
}): Promise<UserStats> => {
  try {
    const url = `${config.apiUrl}/v2/users/${userId}/stats`;

    const response = await apiFetch(url, {
      method: 'GET',
      headers: defaultHeaders(token),
    })

    const data = await processResponse({ response, onAlert })
    return data
  } catch (error) {
    console.error(error)
    if (onAlert) {
      onAlert({
        type: ToastTypes.error,
        text: 'Failed to get user stats',
      })
    }
    throw error
  }
}

const resetPassword = async ({
  email,
  onAlert,
}: {
  email: string
  onAlert: Dispatch<SetStateAction<toastType | null>>
}) => {
  const payload = {
    client_id: config.auth.clientId,
    connection: 'Username-Password-Authentication',
    email,
  }

  return fetch(`https://${config.auth.domain}/dbconnections/change_password`, {
    credentials: 'same-origin',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(payload),
    method: 'POST',
  })
    .then(async (response) => {
      return processResponse({
        auth0: true,
        response,
        onAlert,
      })
    })
    .catch((error) => {
      onAlert({
        text: `${error.name}: ${error.message}`,
        type: ToastTypes.error,
      })
    })
}

const resetUserPassword = async ({
  token,
  userId,
  onAlert,
  intl,
}: {
  token: string
  userId: string
  onAlert: Dispatch<SetStateAction<toastType | null>>
  intl?: any
}) => {
  return apiFetch(`${config.apiUrl}/v1/users/password-reset`, {
    credentials: 'same-origin',
    headers: defaultHeaders(token),
    body: JSON.stringify({ auth0ID: userId }),
    method: 'POST',
  })
    .then(async (response) => {
      return processResponse({
        response,
        onAlert,
        messages: {
          success: {
            text: intl
              ? intl.formatMessage({ id: 'password_reset_success' })
              : 'Password reset email sent successfully',
          },
        },
      })
    })
    .catch((error) => {
      onAlert({
        text: `${error.name}: ${error.message}`,
        type: ToastTypes.error,
      })
    })
}

const getUser = ({
  token,
  onAlert,
  id,
}: {
  token: string
  id?: string
  onAlert: Dispatch<SetStateAction<toastType | null>>
}): Promise<User> => {
  const url = `${config.apiUrl}/v2/users/${id}`;

  return apiFetch(url, {
    credentials: 'same-origin',
    headers: defaultHeaders(token),
    method: 'GET',
  })
    .then((response) => processResponse({ response, onAlert }))
    .catch((error) => {
      onAlert({
        text: `${error.name}: ${error.message}`,
        type: ToastTypes.error,
      })
      throw error
    })
}

const updateUser = ({
  token,
  user,
  onAlert,
  intl,
}: {
  token: string
  user: User
  onAlert: Dispatch<SetStateAction<toastType | null>>
  intl?: any
}): Promise<User> => {
  if (user.licenses && user.licenses.length > 0) {
    (user as any).LicensesType = user.licenses;
  }

  return apiFetch(`${config.apiUrl}/v1/users/${user.id}`, {
    credentials: 'same-origin',
    headers: defaultHeaders(token),
    body: JSON.stringify(user),
    method: 'PATCH',
  })
    .then((response) =>
      processResponse({
        response,
        onAlert,
        messages: {
          success: {
            text: intl
              ? getUpdateUserMessage(user, intl)
              : 'User successfully updated',
          },
        },
      })
    )
    .catch((error) => {
      onAlert({
        text: `${error.name}: ${error.message}`,
        type: ToastTypes.error,
      })
    })
}

const dectivateUser = ({
  token,
  user,
  onAlert,
  intl,
}: {
  token: string
  user: User
  onAlert: Dispatch<SetStateAction<toastType | null>>
  intl?: any
}): Promise<void> => {
  return apiFetch(`${config.apiUrl}/v1/users/deactivate/${user.id}`, {
    credentials: 'same-origin',
    headers: defaultHeaders(token),
    method: 'PATCH',
  })
    .then((response) => {
      processResponse({
        response,
        onAlert,
        messages: {
          success: {
            text: intl
              ? getDeleteUserMessage(user, intl)
              : 'User successfully deactivated',
          },
        },
      })
    })
    .catch((error) => {
      onAlert({
        text: `${error.name}: ${error.message}`,
        type: ToastTypes.error,
      })
    })
}

const getSessions = ({
  token,
  offset = 0,
  limit = 20,
  onAlert,
  partnerId,
  userId,
  sortBy = 'startTimestamp',
  sortOrder = 'DESC',
  sessionStart,
  sessionEnd,
  searchQuery,
  vin,
  dongleId,
  driverId,
  driveType,
  licenseId,
}: {
  token: string
  offset?: number
  limit?: number
  sortBy?: string
  sortOrder?: string
  onAlert: Dispatch<SetStateAction<toastType | null>>
  partnerId?: string
  userId?: string
  sessionStart?: string
  sessionEnd?: string
  searchQuery?: string
  vin?: string
  dongleId?: string
  driverId?: string
  driveType?: number
  licenseId?: string
}): Promise<Session> => {
  const queryStrings = parseQueryStrings({
    limit,
    offset,
    sort_by: sortBy,
    sort_order: sortOrder,
    partner_id: partnerId,
    user_id: userId,
    q: searchQuery,
    session_start: sessionStart,
    session_end: sessionEnd,
    vin,
    dongle_id: dongleId,
    driver_id: driverId,
    drive_type: driveType,
    license_type_id: licenseId,
  })

  const url = `${config.apiUrl}/v2/sessions?${queryStrings}`;

  return apiFetch(url, {
    credentials: 'same-origin',
    headers: defaultHeaders(token),
    method: 'GET',
  })
    .then((response) => {
      return processResponse({ response, onAlert });
    })
    .catch((error) => {
      onAlert({
        text: `${error.name}: ${error.message}`,
        type: ToastTypes.error,
      })
    })
}

const getActiveSessions = ({
  token,
  onAlert,
}: {
  token: string
  onAlert: Dispatch<SetStateAction<toastType | null>>
}): Promise<Session> => {
  return apiFetch(`${config.apiUrl}/v1/sessions/active`, {
    credentials: 'same-origin',
    headers: defaultHeaders(token),
    method: 'GET',
  })
    .then((response) => processResponse({ response, onAlert }))
    .catch((error) => {
      onAlert({
        text: `${error.name}: ${error.message}`,
        type: ToastTypes.error,
      })
    })
}

const getSessionEvents = ({
  token,
  sessionId,
  onAlert,
}: {
  token: string
  sessionId: string
  onAlert: Dispatch<SetStateAction<toastType | null>>
}): Promise<AnnotationsResponse> => {
  return apiFetch(`${config.apiUrl}/v1/sessions/${sessionId}/events`, {
    credentials: 'same-origin',
    headers: defaultHeaders(token),
    method: 'GET',
  })
    .then((response) => processResponse({ response, onAlert }))
    .catch((error) => {
      onAlert({
        text: `${error.name}: ${error.message}`,
        type: ToastTypes.error,
      })
    })
}

const getPartners = ({
  token,
  searchQuery,
  onAlert,
  sortBy,
  sortOrder,
}: {
  token: string
  searchQuery?: string | null
  onAlert: Dispatch<SetStateAction<toastType | null>>
  sortBy?: string
  sortOrder?: string
}): Promise<Partner[]> => {
  const queryStrings = parseQueryStrings({
    sort_by: sortBy,
    sort_order: sortOrder,
    q: searchQuery,
  })

  return apiFetch(`${config.apiUrl}/v1/partners?${queryStrings}`, {
    credentials: 'same-origin',
    headers: defaultHeaders(token),
    method: 'GET',
  })
    .then((response) => processResponse({ response, onAlert }))
    .catch((error) => {
      onAlert({
        text: `${error.name}: ${error.message}`,
        type: ToastTypes.error,
      })
    })
}

const createPartner = ({
  token,
  createPartner,
  onAlert,
}: {
  token: string
  createPartner: CreatePartner
  onAlert: Dispatch<SetStateAction<toastType | null>>
}): Promise<Partner[]> => {
  // @ts-ignore id must not be set when creating
  const requestPartner: Partner = {
    name: createPartner.name,
    language: createPartner.language,
    simEnabled: createPartner.simEnabled,
    mainContact: null,
    mainContactId: null,
    plans: [createPartner.plans],
  }
  return apiFetch(`${config.apiUrl}/v1/partners`, {
    credentials: 'same-origin',
    headers: defaultHeaders(token),
    body: JSON.stringify(requestPartner),
    method: 'POST',
  })
    .then((response) => processResponse({ response, onAlert }))
    .catch((error) => {
      onAlert({
        text: `${error.name}: ${error.message}`,
        type: ToastTypes.error,
      })
    })
}

const updatePartner = ({
  token,
  updatePartner,
  onAlert,
}: {
  token: string
  updatePartner: UpdatePartner
  onAlert: Dispatch<SetStateAction<toastType | null>>
}): Promise<Partner> => {
  // @ts-ignore id must not be set when updating
  const requestPartner: Partner = {
    name: updatePartner.name,
    language: updatePartner.language,
    mainContact: updatePartner.mainContact,
    mainContactId: updatePartner.mainContactId,
    simEnabled: updatePartner.simEnabled,
    plans: [updatePartner.plans]
  }

  return apiFetch(`${config.apiUrl}/v1/partners/${updatePartner.id}`, {
    credentials: 'same-origin',
    headers: defaultHeaders(token),
    body: JSON.stringify(requestPartner),
    method: 'PATCH',
  })
    .then((response) => processResponse({ response, onAlert }))
    .catch((error) => {
      onAlert({
        text: `${error.name}: ${error.message}`,
        type: ToastTypes.error,
      })
    })
}

const getPairingRequests = ({
  token,
  onAlert,
  searchQuery,
  sortBy,
  orderBy,
  limit,
  offset,
  status,
}: {
  token: string
  searchQuery?: string
  sortBy?: string
  orderBy?: string
  limit?: number
  offset?: number
  onAlert: Dispatch<SetStateAction<toastType | null>>
  status?: string
}): Promise<PairingResponse> => {
  const queryStrings = parseQueryStrings({
    limit,
    offset,
    sort_by: sortBy,
    order_by: orderBy,
    q: searchQuery,
    status,
  })

  return apiFetch(`${config.apiUrl}/v1/pairings?${queryStrings}`, {
    credentials: 'same-origin',
    headers: defaultHeaders(token),
    method: 'GET',
  })
    .then((response) => processResponse({ response, onAlert }))
    .catch((error) => {
      onAlert({
        text: `${error.name}: ${error.message}`,
        type: ToastTypes.error,
      })
    })
}

const updatePairingRequest = ({
  token,
  id,
  status,
  onAlert,
}: {
  token: string
  id: string
  status: string
  onAlert: Dispatch<SetStateAction<toastType | null>>
}): Promise<void> => {
  return apiFetch(`${config.apiUrl}/v1/pairing/${id}`, {
    credentials: 'same-origin',
    headers: defaultHeaders(token),
    body: JSON.stringify({ status }),
    method: 'PUT',
  })
    .then((response) => processResponse({ response, onAlert }))
    .catch(() => {
      // onAlert({
      //   text: `${error.name}: ${error.message}`,
      //   type: ToastTypes.error,
      // })
    })
}

const createPairingRequest = ({
  token,
  pairingRequest,
  onAlert,
}: {
  token: string
  pairingRequest: CreatePairingRequest
  onAlert: Dispatch<SetStateAction<toastType | null>>
}): Promise<void> => {
  return apiFetch(`${config.apiUrl}/v1/pairing`, {
    credentials: 'same-origin',
    headers: defaultHeaders(token),
    body: JSON.stringify(pairingRequest),
    method: 'POST',
  }).then((response) =>
    processResponse({
      response,
      onAlert,
      messages: {
        success: {
          text: 'Pairing request successfully created',
        },
      },
    })
  )
}

const getDevices = ({
  token,
  onAlert,
  searchQuery,
  paired,
  sortBy,
  orderBy,
  partnerId,
}: {
  token: string
  searchQuery?: string
  paired?: boolean
  sortBy?: string
  orderBy?: string
  partnerId?: string
  onAlert: Dispatch<SetStateAction<toastType | null>>
}): Promise<Device[]> => {
  const queryStrings = parseQueryStrings({
    sort_by: sortBy,
    paired,
    sort_order: orderBy,
    q: searchQuery,
    partnerId,
  })

  return apiFetch(`${config.apiUrl}/v1/devices?${queryStrings}`, {
    credentials: 'same-origin',
    headers: defaultHeaders(token),
    method: 'GET',
  })
    .then((response) => processResponse({ response, onAlert }))
    .catch((error) => {
      onAlert({
        text: `${error.name}: ${error.message}`,
        type: ToastTypes.error,
      })
    })
}

const getDevice = ({
  token,
  dongleId,
  onAlert,
}: {
  token: string
  dongleId?: string
  onAlert: Dispatch<SetStateAction<toastType | null>>
}): Promise<Device> => {
  return apiFetch(`${config.apiUrl}/v1/devices/${dongleId}`, {
    credentials: 'same-origin',
    headers: defaultHeaders(token),
    method: 'GET',
  })
    .then((response) => processResponse({ response, onAlert }))
    .catch((error) => {
      onAlert({
        text: `${error.name}: ${error.message}`,
        type: ToastTypes.error,
      })
    })
}

const updateDevice = ({
  token,
  device,
  onAlert,
}: {
  token: string
  device: Device
  onAlert: Dispatch<SetStateAction<toastType | null>>
}): Promise<Device> => {
  return apiFetch(`${config.apiUrl}/v1/devices/${device.dongleId}`, {
    credentials: 'same-origin',
    headers: defaultHeaders(token),
    body: JSON.stringify(device),
    method: 'PATCH',
  })
    .then((response) => processResponse({ response, onAlert }))
    .catch((error) => {
      onAlert({
        text: `${error.name}: ${error.message}`,
        type: ToastTypes.error,
      })
    })
}

const deleteDevice = ({
  token,
  dongleId,
  onAlert,
}: {
  token: string
  dongleId: string
  onAlert: Dispatch<SetStateAction<toastType | null>>
}): Promise<void> => {
  return apiFetch(`${config.apiUrl}/v1/devices/${dongleId}`, {
    credentials: 'same-origin',
    headers: defaultHeaders(token),
    method: 'DELETE',
  })
    .then(() => { })
    .catch((error) => {
      onAlert({
        text: `${error.name}: ${error.message}`,
        type: ToastTypes.error,
      })
    })
}

const forceStopSession = ({
  token,
  sessionId,
  onAlert,
}: {
  token: string
  sessionId: string
  onAlert: Dispatch<SetStateAction<toastType | null>>
}): Promise<void> => {
  return apiFetch(
    `${config.apiUrl}/v1/activeSessions/${sessionId}?force=true`,
    {
      credentials: 'same-origin',
      headers: defaultHeaders(token),
      method: 'DELETE',
    }
  )
    .then(() => { })
    .catch((error) => {
      onAlert({
        text: `${error.name}: ${error.message}`,
        type: ToastTypes.error,
      })
    })
}

const startDevice = ({
  token,
  dongleId,
  onAlert,
}: {
  token: string
  dongleId: string
  onAlert: Dispatch<SetStateAction<toastType | null>>
}): Promise<void> => {
  return apiFetch(`${config.apiUrl}/v1/commands/startDevice`, {
    credentials: 'same-origin',
    headers: defaultHeaders(token),
    body: JSON.stringify({ dongleId }),
    method: 'PUT',
  })
    .then(() => { })
    .catch((error) => {
      onAlert({
        text: `${error.name}: ${error.message}`,
        type: ToastTypes.error,
      })
    })
}

const getVehicles = ({
  token,
  onAlert,
  searchQuery,
  limit = 20,
  offset,
  sortBy = 'vin',
  sortOrder = 'DESC',
  paired,
  partnerId,
}: {
  token: string
  searchQuery?: string
  onAlert: Dispatch<SetStateAction<toastType | null>>
  limit?: number
  offset?: number
  sortBy?: string
  sortOrder?: string
  paired?: boolean
  partnerId?: string
}): Promise<VehicleResponse> => {
  const queryStrings = parseQueryStrings({
    sort_by: sortBy,
    sort_order: sortOrder,
    q: searchQuery,
    partnerId,
    paired,
    offset,
    limit,
  })

  return apiFetch(`${config.apiUrl}/v1/vehicles?${queryStrings}`, {
    credentials: 'same-origin',
    headers: defaultHeaders(token),
    method: 'GET',
  })
    .then((response) => processResponse({ response, onAlert }))
    .catch((error) => {
      onAlert({
        text: `${error.name}: ${error.message}`,
        type: ToastTypes.error,
      })
    })
}

const updateVehicle = ({
  token,
  vehicle,
  onAlert,
}: {
  token: string
  vehicle: CreateVehicle
  onAlert: Dispatch<SetStateAction<toastType | null>>
}): Promise<CreateVehicle> => {
  return apiFetch(`${config.apiUrl}/v1/vehicles/${vehicle.vin}`, {
    credentials: 'same-origin',
    headers: defaultHeaders(token),
    body: JSON.stringify(vehicle),
    method: 'PATCH',
  })
    .then((response) => processResponse({ response, onAlert }))
    .catch((error) => {
      onAlert({
        text: `${error.name}: ${error.message}`,
        type: ToastTypes.error,
      })
    })
}

const createVehicle = ({
  token,
  vehicle,
  onAlert,
}: {
  token: string
  vehicle: CreateVehicle
  onAlert: Dispatch<SetStateAction<toastType | null>>
}): Promise<Device> => {
  return apiFetch(`${config.apiUrl}/v1/vehicles/`, {
    credentials: 'same-origin',
    headers: defaultHeaders(token),
    body: JSON.stringify(vehicle),
    method: 'POST',
  })
    .then((response) =>
      processResponse({
        response,
        onAlert,
        messages: {
          success: {
            text: 'Vehicle successfully created',
          },
        },
      })
    )
    .catch(() => {
      // TODO: Bring back when there is response body
      // onAlert({
      //   text: `${error.name}: ${error.message}`,
      //   type: ToastTypes.error,
      // })
    })
}

const getVehicle = ({
  token,
  vin,
  onAlert,
}: {
  token: string
  vin?: string
  onAlert: Dispatch<SetStateAction<toastType | null>>
}): Promise<Vehicle> => {
  return apiFetch(`${config.apiUrl}/v1/vehicles/${vin}`, {
    credentials: 'same-origin',
    headers: defaultHeaders(token),
    method: 'GET',
  })
    .then((response) => processResponse({ response, onAlert }))
    .catch((error) => {
      onAlert({
        text: `${error.name}: ${error.message}`,
        type: ToastTypes.error,
      })
    })
}

const getSimDevices = ({
  token,
  onAlert,
  sortBy,
  sortOrder,
  offset,
  partnerId,
}: {
  token: string
  partnerId?: string
  sortBy?: string
  sortOrder?: string
  offset?: number
  onAlert: Dispatch<SetStateAction<toastType | null>>
}): Promise<SimRigs> => {
  const queryStrings = parseQueryStrings({
    sort_by: sortBy,
    sort_order: sortOrder,
    offset,
    partner_id: partnerId,
  })

  return apiFetch(`${config.apiUrl}/v1/sim/devices?${queryStrings}`, {
    credentials: 'same-origin',
    headers: defaultHeaders(token),
    method: 'GET',
  })
    .then((response) => processResponse({ response, onAlert }))
    .catch((error) => {
      onAlert({
        text: `${error.name}: ${error.message}`,
        type: ToastTypes.error,
      })
    })
}

const getSimulationDrives = ({
  token,
  onAlert,
  searchQuery,
  partnerId,
  userId,
  simDeviceId,
  limit = 20,
  offset,
  sortBy,
  sortOrder = 'DESC',
  sessionStart,
  sessionEnd,
}: {
  token: string
  searchQuery?: string
  partnerId?: string
  userId?: string
  simDeviceId?: string
  limit?: number
  offset?: number
  sortBy?: string
  sortOrder?: string
  sessionStart?: string
  sessionEnd?: string
  onAlert: Dispatch<SetStateAction<toastType | null>>
}): Promise<SimulationDrives> => {
  const queryStrings = parseQueryStrings({
    sort_by: sortBy,
    sort_order: sortOrder,
    q: searchQuery,
    offset,
    limit,
    partner_id: partnerId,
    user_id: userId,
    sim_device_id: simDeviceId,
    session_start: sessionStart,
    session_end: sessionEnd,
    local: true
  })
  return apiFetch(`${config.apiUrl}/v1/sim/sessions?${queryStrings}`, {
    credentials: 'same-origin',
    headers: defaultHeaders(token),
    method: 'GET',
  })
    .then((response) => processResponse({ response, onAlert }))
    .catch((error) => {
      onAlert({
        text: `${error.name}: ${error.message}`,
        type: ToastTypes.error,
      })
    })
}

const getSimProgress = ({
  token,
  onAlert,
  userId,
  partnerId,
}: {
  token: string
  userId: string
  partnerId?: string
  onAlert: Dispatch<SetStateAction<toastType | null>>
}): Promise<SimProgress> => {
  const queryStrings = parseQueryStrings({
    user_id: userId,
    partner_id: partnerId,
  })

  return apiFetch(`${config.apiUrl}/v1/sim/progress?${queryStrings}`, {
    credentials: 'same-origin',
    headers: defaultHeaders(token),
    method: 'GET',
  })
    .then((response) => processResponse({ response, onAlert }))
    .catch((error) => {
      onAlert({
        text: `${error.name}: ${error.message}`,
        type: ToastTypes.error,
      })
    })
}

const getActiveSimulationDrives = ({
  token,
  onAlert,
  partnerId,
  userId,
  simDeviceId,
  limit = 20,
  offset,
  sortOrder = 'ASC',
}: {
  token: string
  partnerId?: string
  userId?: string
  simDeviceId?: string
  limit?: number
  offset?: number
  onAlert: Dispatch<SetStateAction<toastType | null>>
  sortOrder?: string
}): Promise<SimulationDrives> => {
  const queryStrings = parseQueryStrings({
    offset,
    limit,
    sort_order: sortOrder,
    partner_id: partnerId,
    user_id: userId,
    sim_device_id: simDeviceId,
  })
  return apiFetch(`${config.apiUrl}/v1/sim/sessions/active?${queryStrings}`, {
    credentials: 'same-origin',
    headers: defaultHeaders(token),
    method: 'GET',
  })
    .then((response) => processResponse({ response, onAlert }))
    .catch((error) => {
      onAlert({
        text: `${error.name}: ${error.message}`,
        type: ToastTypes.error,
      })
    })
}

const deleteSim = ({
  token,
  id,
  onAlert,
}: {
  token: string
  id: string
  onAlert: Dispatch<SetStateAction<toastType | null>>
}): Promise<void> => {
  return apiFetch(`${config.apiUrl}/v1/sim/sessions/active/${id}`, {
    credentials: 'same-origin',
    headers: defaultHeaders(token),
    method: 'DELETE',
  })
    .then(() => { })
    .catch((error) => {
      onAlert({
        text: `${error.name}: ${error.message}`,
        type: ToastTypes.error,
      })
    })
}

const updateSimRig = ({
  token,
  simRig,
  onAlert,
}: {
  token: string
  simRig: SimRigsData
  onAlert: Dispatch<SetStateAction<toastType | null>>
}): Promise<SimRigsData> => {
  return apiFetch(`${config.apiUrl}/v1/sim/devices/${simRig.id}`, {
    credentials: 'same-origin',
    headers: defaultHeaders(token),
    body: JSON.stringify(simRig),
    method: 'PATCH',
  })
    .then((response) =>
      processResponse({
        response,
        onAlert,
        messages: {
          success: {
            text: 'Sim successfully updated',
          },
        },
      })
    )
    .catch((error) => {
      onAlert({
        text: `${error.name}: ${error.message}`,
        type: ToastTypes.error,
      })
    })
}

const getSimModuleCatalog = ({
  token,
  onAlert,
}: {
  token: string
  onAlert: Dispatch<SetStateAction<toastType | null>>
}): Promise<SimModuleCatalogResponse> => {
  return fetch(`${config.apiUrl}/v1/sim/modules/catalog`, {
    credentials: 'same-origin',
    headers: defaultHeaders(token),
    method: 'GET',
  })
    .then((response) => processResponse({ response, onAlert }))
    .catch((error) => {
      onAlert({
        text: `${error.name}: ${error.message}`,
        type: ToastTypes.error,
      })
    })
}

const updateSimModuleActivation = ({
  token,
  moduleIds,
  active,
  onAlert,
}: {
  token: string
  moduleIds: string[]
  active: boolean
  onAlert: Dispatch<SetStateAction<toastType | null>>
}): Promise<SimModuleCatalogResponse> => {
  return fetch(`${config.apiUrl}/v1/sim/modules/activation`, {
    credentials: 'same-origin',
    headers: defaultHeaders(token),
    body: JSON.stringify({ moduleIds, active }),
    method: 'PATCH',
  })
    .then((response) =>
      processResponse({
        response,
        onAlert,
        messages: {
          success: {
            text: 'Sim modules updated successfully',
          },
        },
      })
    )
    .catch((error) => {
      onAlert({
        text: `${error.name}: ${error.message}`,
        type: ToastTypes.error,
      })
    })
}

const updateSimModuleCustom = ({
  token,
  moduleIds,
  custom,
  onAlert,
}: {
  token: string
  moduleIds: string[]
  custom: boolean
  onAlert: Dispatch<SetStateAction<toastType | null>>
}): Promise<SimModuleCatalogResponse> => {
  return fetch(`${config.apiUrl}/v1/sim/modules/custom`, {
    credentials: 'same-origin',
    headers: defaultHeaders(token),
    body: JSON.stringify({ moduleIds, custom }),
    method: 'PATCH',
  })
    .then((response) =>
      processResponse({
        response,
        onAlert,
        messages: {
          success: {
            text: 'Sim modules updated successfully',
          },
        },
      })
    )
    .catch((error) => {
      onAlert({
        text: `${error.name}: ${error.message}`,
        type: ToastTypes.error,
      })
    })
}

const getSimModuleAllowlist = ({
  token,
  moduleId,
  onAlert,
}: {
  token: string
  moduleId: string
  onAlert: Dispatch<SetStateAction<toastType | null>>
}): Promise<SimModuleAllowlistResponse> => {
  return fetch(`${config.apiUrl}/v1/sim/modules/${encodeURIComponent(moduleId)}/allowlist`, {
    credentials: 'same-origin',
    headers: defaultHeaders(token),
    method: 'GET',
  })
    .then((response) => processResponse({ response, onAlert }))
    .catch((error) => {
      onAlert({
        text: `${error.name}: ${error.message}`,
        type: ToastTypes.error,
      })
    })
}

const updateSimModuleAllowlist = ({
  token,
  moduleId,
  partnerIds,
  onAlert,
}: {
  token: string
  moduleId: string
  partnerIds: string[]
  onAlert: Dispatch<SetStateAction<toastType | null>>
}): Promise<SimModuleAllowlistResponse> => {
  return fetch(`${config.apiUrl}/v1/sim/modules/${encodeURIComponent(moduleId)}/allowlist`, {
    credentials: 'same-origin',
    headers: defaultHeaders(token),
    body: JSON.stringify({ partnerIds }),
    method: 'PATCH',
  })
    .then((response) =>
      processResponse({
        response,
        onAlert,
        messages: {
          success: {
            text: 'Module whitelist updated successfully',
          },
        },
      })
    )
    .catch((error) => {
      onAlert({
        text: `${error.name}: ${error.message}`,
        type: ToastTypes.error,
      })
    })
}

const getSimMapConfig = ({
  token,
  partnerId,
  onAlert,
}: {
  token: string
  partnerId: string
  onAlert: Dispatch<SetStateAction<toastType | null>>
}): Promise<SimMapConfigResponse> => {
  return fetch(`${config.apiUrl}/v1/sim/partners/${partnerId}/map-config`, {
    credentials: 'same-origin',
    headers: defaultHeaders(token),
    method: 'GET',
  })
    .then((response) => processResponse({ response, onAlert }))
    .catch((error) => {
      onAlert({
        text: `${error.name}: ${error.message}`,
        type: ToastTypes.error,
      })
    })
}

const updateSimMapConfig = ({
  token,
  partnerId,
  moduleIds,
  onAlert,
}: {
  token: string
  partnerId: string
  moduleIds: string[]
  onAlert: Dispatch<SetStateAction<toastType | null>>
}): Promise<SimMapConfigResponse> => {
  return fetch(`${config.apiUrl}/v1/sim/partners/${partnerId}/map-config`, {
    credentials: 'same-origin',
    headers: defaultHeaders(token),
    body: JSON.stringify({ moduleIds }),
    method: 'PATCH',
  })
    .then((response) =>
      processResponse({
        response,
        onAlert,
        messages: {
          success: {
            text: 'Module config updated successfully',
          },
        },
      })
    )
    .catch((error) => {
      onAlert({
        text: `${error.name}: ${error.message}`,
        type: ToastTypes.error,
      })
    })
}

const getVideoStreams = ({
  token,
  onAlert,
  sessionId,
}: {
  token: string
  onAlert: Dispatch<SetStateAction<toastType | null>>
  sessionId: string
}): Promise<VideoStreams> => {
  const queryStrings = parseQueryStrings({
    session_id: sessionId,
  })
  return apiFetch(`${config.apiUrl}/v1/videoStreams?${queryStrings}`, {
    credentials: 'same-origin',
    headers: defaultHeaders(token),
    method: 'GET',
  })
    .then((response) => processResponse({ response, onAlert }))
    .catch((error) => {
      onAlert?.({
        text: `${error.name}: ${error.message}`,
        type: ToastTypes.error,
      })
    })
}

const getSessionInfo = ({
  token,
  onAlert,
  sessionId,
}: {
  token: string
  onAlert: Dispatch<SetStateAction<toastType | null>>
  sessionId: string
}): Promise<SessionInfo> => {
  return apiFetch(`${config.apiUrl}/v1/sessions/${sessionId}/embodied`, {
    credentials: 'same-origin',
    headers: defaultHeaders(token),
    method: 'GET',
  })
    .then((response) => processResponse({ response, onAlert }))
    .catch((error) => {
      onAlert?.({
        text: `${error.name}: ${error.message}`,
        type: ToastTypes.error,
      })
    })
}

const getMetadata = ({
  url,
  onAlert,
}: {
  onAlert: Dispatch<SetStateAction<toastType | null>>
  url: string
}): Promise<any> => {
  return apiFetch(url, {
    credentials: 'same-origin',
    method: 'GET',
  })
    .then(async (response) => {
      return response
    })
    .catch((error) => {
      onAlert?.({
        text: `${error.name}: ${error.message}`,
        type: ToastTypes.error,
      })
    })
}

const getMetadataByRange = ({
  url,
  offset,
  length,
}: {
  url: string
  offset: number
  length: number
}): Promise<Response> => {
  const headers = new Headers({
    range: `bytes=${offset}-${offset + (length - 1)}`,
  })
  return apiFetch(url, { headers })
}

const getIncidentOutcomes = ({
  token,
  onAlert,
}: {
  token: string
  onAlert: Dispatch<SetStateAction<toastType | null>>
}): Promise<IncidentOutcomes> => {
  return apiFetch(`${config.apiUrl}/v1/incidentOutcomes`, {
    credentials: 'same-origin',
    headers: defaultHeaders(token),
    method: 'GET',
  })
    .then((response) => processResponse({ response, onAlert }))
    .catch((error) => {
      onAlert?.({
        text: `${error.name}: ${error.message}`,
        type: ToastTypes.error,
      })
    })
}

const getMetrics = ({
  token,
  onAlert,
}: {
  token: string
  onAlert: Dispatch<SetStateAction<toastType | null>>
}): Promise<MetricsData> => {
  return apiFetch(`${config.apiUrl}/v1/data/metrics`, {
    credentials: 'same-origin',
    headers: defaultHeaders(token),
    method: 'GET',
  })
    .then((response) => processResponse({ response, onAlert }))
    .catch((error) => {
      onAlert?.({
        text: `${error.name}: ${error.message}`,
        type: ToastTypes.error,
      })
    })
}
const getTheoryClasses = ({
  token,
  onAlert
}: {
  token: string
  onAlert: Dispatch<SetStateAction<toastType | null>>
}): Promise<TheoryClassesResponse> => {
  return apiFetch(`${config.apiUrl}/v1/theory/class`, {
    credentials: 'same-origin',
    headers: defaultHeaders(token),
    method: 'GET'
  })
    .then((response) => processResponse({ response, onAlert }))
    .catch((error) => {
      onAlert?.({
        text: `${error.name}: ${error.message}`,
        type: ToastTypes.error,
      })
    })
}

const getFeatureFlags = ({
  token,
}: {
  token: string
}): Promise<FeatureFlags> => {
  return apiFetch(`${config.apiUrl}/v1/featureflags`, {
    credentials: 'same-origin',
    headers: defaultHeaders(token),
    method: 'GET',
  })
    .then((response) => response.json())
    .catch((error) => {
      console.error("Couldn't fetch feature flags", error)
    })
}

const getPartnerSettings = ({
  token,
  onAlert
}: {
  token: string
  onAlert: Dispatch<SetStateAction<toastType | null>>
}): Promise<PartnerSettings> => {
  return apiFetch(`${config.apiUrl}/v1/partners/settings`, {
    credentials: 'same-origin',
    headers: defaultHeaders(token),
    method: 'GET',
  })
    .then((response) => response.json())
    .catch((error) => {
      onAlert?.({
        text: `${error.name}: ${error.message}`,
        type: ToastTypes.error,
      })
    })
}
const updatePartnerSettings = ({
  token,
  editedSettings,
  onAlert,
  intl
}: {
  token: string
  editedSettings: PartnerSettings
  onAlert: Dispatch<SetStateAction<toastType | null>>
  intl: IntlShape
}): Promise<void> => {
  return apiFetch(`${config.apiUrl}/v1/partners/settings`, {
    credentials: 'same-origin',
    headers: defaultHeaders(token),
    body: JSON.stringify(editedSettings),
    method: 'PUT',
  })
    .then((response) => processResponse({
      response, onAlert, messages: {
        success: {
          text: intl.formatMessage({ id: 'partner_settings_updated' }),
        },
      },
    }))
    .catch((error) => {
      onAlert?.({
        text: `${error.name}: ${error.message}`,
        type: ToastTypes.error,
      })
    })
}

const getSwagger = ({
  token,
  onAlert,
}: {
  token: string
  onAlert: Dispatch<SetStateAction<toastType | null>>
}): Promise<string> => {
  return apiFetch(`${config.apiUrl}/v1/swagger`, {
    credentials: 'same-origin',
    headers: defaultHeaders(token),
    method: 'GET',
  })
    .then((response) => processResponse({ response, onAlert, messages: {}, auth0: true }))
    .catch((error) => {
      onAlert({
        text: `${error.name}: ${error.message}`,
        type: ToastTypes.error,
      })
    })
}

const getFeatureFlagAdmin = ({
  token,
}: {
  token: string
}): Promise<FeatureFlagAdmin[]> => {
  return apiFetch(`${config.apiUrl}/v1/featureflags/admin`, {
    credentials: 'same-origin',
    headers: defaultHeaders(token),
    method: 'GET',
  })
    .then((response) => response.json())
    .catch((error) => {
      console.error("Couldn't fetch feature flags", error)
    })
}

const updateFeatureFlag = ({
  token,
  featureFlag,
}: {
  token: string
  featureFlag: FeatureFlagEditableAdmin
}): Promise<any> => {
  // @ts-ignore id must not be set when updating
  const requestFeatureFlag: FeatureFlagAdmin = {
    description: featureFlag.description,
    enabled: featureFlag.enabled
  }

  return apiFetch(`${config.apiUrl}/v1/featureflag/${featureFlag.id}`, {
    credentials: 'same-origin',
    headers: defaultHeaders(token),
    body: JSON.stringify(requestFeatureFlag),
    method: 'PATCH',
  })
    .then((response) => response.status === 200)
    .catch((error) => {
      console.error("Couldn't fetch feature flags", error)
    })
}

const createFeatureFlag = ({
  token,
  createFeatureFlag,
  onAlert,
}: {
  token: string
  createFeatureFlag: CreateFeatureFlag
  onAlert: Dispatch<SetStateAction<toastType | null>>
}): Promise<FeatureFlagAdmin> => {
  const requestPartner: CreateFeatureFlag = {
    name: createFeatureFlag.name,
    description: createFeatureFlag.description,
    enabled: createFeatureFlag.enabled,
  }
  return apiFetch(`${config.apiUrl}/v1/featureflag`, {
    credentials: 'same-origin',
    headers: defaultHeaders(token),
    body: JSON.stringify(requestPartner),
    method: 'POST',
  })
    .then((response) => processResponse({ response, onAlert }))
    .catch((error) => {
      onAlert({
        text: `${error.name}: ${error.message}`,
        type: ToastTypes.error,
      })
    })
}

type FinishLicenseRequest = {
  token: string
  onAlert: Dispatch<SetStateAction<toastType | null>>
  userId: string
  userLicenseId: string
}

const finishLicense = ({
  token,
  onAlert,
  userId,
  userLicenseId,
}: FinishLicenseRequest): Promise<void> => {
  const encodedUserId = encodeURIComponent(userId)
  return apiFetch(`${config.apiUrl}/v1/users/${encodedUserId}/finish_license/${userLicenseId}`, {
    credentials: 'same-origin',
    headers: defaultHeaders(token),
    method: 'PATCH',
  })
    .then((response) => processResponse({ response, onAlert, messages: {}, auth0: true }))
    .catch((error) => {
      onAlert({
        text: `${error.name}: ${error.message}`,
        type: ToastTypes.error,
      })
    })
}

type DeactivateLicenseRequest = {
  token: string
  onAlert: Dispatch<SetStateAction<toastType | null>>
  userId: string
  userLicenseId: string
}

const deactivateLicense = ({
  token,
  onAlert,
  userId,
  userLicenseId,
}: DeactivateLicenseRequest): Promise<void> => {
  const encodedUserId = encodeURIComponent(userId)
  return apiFetch(`${config.apiUrl}/v1/users/${encodedUserId}/deactivate_license/${userLicenseId}`, {
    credentials: 'same-origin',
    headers: defaultHeaders(token),
    method: 'PATCH',
  })
    .then((response) => processResponse({ response, onAlert, messages: {}, auth0: true }))
    .catch((error) => {
      onAlert({
        text: `${error.name}: ${error.message}`,
        type: ToastTypes.error,
      })
    })
}

type PatchLicenseRequest = {
  token: string
  onAlert: Dispatch<SetStateAction<toastType | null>>
  userId: string
  userLicenseId: string
  trainingPathId: string
}

const patchUserLicense = ({
  token,
  onAlert,
  userId,
  userLicenseId,
  trainingPathId,
}: PatchLicenseRequest): Promise<void> => {
  return apiFetch(
    `${config.apiUrl}/v1/users/${encodeURIComponent(userId)}/license/${encodeURIComponent(userLicenseId)}`,
    {
      credentials: 'same-origin',
      headers: defaultHeaders(token),
      body: JSON.stringify({ trainingPathId }),
      method: 'PATCH',
    }
  )
    .then((response) => processResponse({ response, onAlert, auth0: true }))
    .catch((error) => {
      onAlert({
        text: `${error.name}: ${error.message}`,
        type: ToastTypes.error,
      })
    })
}

type AddLicenseRequest = {
  token: string
  onAlert: Dispatch<SetStateAction<toastType | null>>
  userId: string
  licenseTypeId: string
  learningPathId?: string
}

const addUserLicense = ({
  token,
  onAlert,
  userId,
  licenseTypeId,
  learningPathId,
}: AddLicenseRequest): Promise<void> => {
  const encodedUserId = encodeURIComponent(userId)
  const payload: { licenseTypeId: string; learningPathId?: string } = { licenseTypeId }
  if (learningPathId) payload.learningPathId = learningPathId

  return apiFetch(`${config.apiUrl}/v1/users/${encodedUserId}/licenses`, {
    credentials: 'same-origin',
    headers: defaultHeaders(token),
    body: JSON.stringify(payload),
    method: 'POST',
  })
    .then((response) => processResponse({ response, onAlert, messages: {}, auth0: true }))
    .catch((error) => {
      onAlert({
        text: `${error.name}: ${error.message}`,
        type: ToastTypes.error,
      })
    })
}

const getLicenseTypes = ({
  token,
  onAlert,
}: {
  token: string
  onAlert: Dispatch<SetStateAction<toastType | null>>
}): Promise<LicenseType[]> => {
  return apiFetch(`${config.apiUrl}/v1/licenses`, {
    credentials: 'same-origin',
    headers: defaultHeaders(token),
    method: 'GET',
  })
    .then((response) => processResponse({ response, onAlert }))
    .catch((error) => {
      onAlert({
        text: `${error.name}: ${error.message}`,
        type: ToastTypes.error,
      })
    })
}

const getPruefungAttempts = ({
  token,
  license,
  page,
  passed,
  onAlert,
}: {
  token: string
  license: string
  page?: number
  passed?: boolean
  onAlert: Dispatch<SetStateAction<toastType | null>>
}): Promise<PruefungAttemptResponse> => {
  const queryStrings = parseQueryStrings({
    userLicenseId: license,
    p: page || 1,
    passed: passed || undefined,
  })

  return apiFetch(`${config.apiUrl}/v2/pruefung/attempts?${queryStrings}`, {
    credentials: 'same-origin',
    headers: defaultHeaders(token),
    method: 'GET',
  })
    .then((response) => processResponse({ response, onAlert }))
    .catch((error) => {
      onAlert({
        text: `${error.name}: ${error.message}`,
        type: ToastTypes.error,
      })
    })
}


const syncSimulators = ({
  token,
  partnerId,
  onAlert,
}: {
  token: string
  partnerId: string
  onAlert: Dispatch<SetStateAction<toastType | null>>
}): Promise<void> => {
  return apiFetch(`${config.apiUrl}/v1/sim/flow/${partnerId}/sync`, {
    credentials: 'same-origin',
    headers: defaultHeaders(token),
    body: JSON.stringify({ PartnerID: partnerId }),
    method: 'POST',
  })
    .then((response) =>
      processResponse({
        response,
        onAlert,
        messages: {
          success: {
            text: 'Simulators synced successfully',
          },
        },
      })
    )
    .catch((error) => {
      onAlert({
        text: `${error.name}: ${error.message}`,
        type: ToastTypes.error,
      })
    })
}

const inviteStudent = ({
  token,
  student,
  onAlert,
}: {
  token: string
  student: InviteStudent
  onAlert: Dispatch<SetStateAction<toastType | null>>
}): Promise<User> => {
  return apiFetch(`${config.apiUrl}/v1/users/invite/student`, {
    credentials: 'same-origin',
    headers: defaultHeaders(token),
    body: JSON.stringify(student),
    method: 'POST',
  })
    .then((response) =>
      processResponse({
        response,
        onAlert,
        messages: {
          success: {
            text: `Student ${student.firstName} ${student.lastName} added successfully`,
          },
        },
      })
    )
    .catch((error) => {
      onAlert({
        text: `${error.name}: ${error.message}`,
        type: ToastTypes.error,
      })
    })
}

const syncVehicles = ({
  token,
  partnerId,
  onAlert,
}: {
  token: string
  partnerId: string
  onAlert: Dispatch<SetStateAction<toastType | null>>
}): Promise<void> => {
  return apiFetch(`${config.apiUrl}/v1/vehicles/flow/${partnerId}/sync`, {
    credentials: 'same-origin',
    headers: defaultHeaders(token),
    method: 'POST',
  })
    .then((response) =>
      processResponse({
        response,
        onAlert,
        messages: {
          success: {
            text: 'Vehicles synced successfully',
          },
        },
      })
    )
    .catch((error) => {
      onAlert({
        text: `${error.name}: ${error.message}`,
        type: ToastTypes.error,
      })
    })
}

export {
  addUserLicense,
  createExamResult,
  inviteStudent,
  createPairingRequest,
  createPartner,
  createUser,
  createVehicle,
  createFeatureFlag,
  deleteDevice,
  deleteSim,
  dectivateUser,
  forceStopSession,
  getActiveSessions,
  getActiveSimulationDrives,
  getPairingRequests,
  getDevice,
  getDevices,
  getIncidentOutcomes,
  getLicenseTypes,
  getLoggedInUser,
  getMetadata,
  getMetadataByRange,
  getMetrics,
  getPartnerStats,
  getPartners,
  getPruefungAttempts,
  getSessionById,
  getSessionEvents,
  getSessionInfo,
  getSessions,
  getSimDevices,
  getSimMapConfig,
  getSimModuleCatalog,
  updateSimModuleActivation,
  updateSimModuleCustom,
  getSimModuleAllowlist,
  updateSimModuleAllowlist,
  getSimulationDrives,
  getSimProgress,
  getSwagger,
  getFeatureFlagAdmin,
  getUser,
  getUserStats,
  getUsers,
  getStudents,
  getVideoStreams,
  getVehicle,
  getVehicles,
  getTheoryClasses,
  getFeatureFlags,
  getPartnerSettings,
  resetPassword,
  resetUserPassword,
  startDevice,
  updateDevice,
  updatePartner,
  updateUser,
  updatePairingRequest,
  getInstructorData,
  updateSimMapConfig,
  updateSimRig,
  updateVehicle,
  updatePartnerSettings,
  updateFeatureFlag,
  finishLicense,
  deactivateLicense,
  patchUserLicense,
  syncSimulators,
  syncVehicles,
}
